import CoupaEditor from './CoupaEditor';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { Loader2, CheckCircle, XCircle, Clock, Eye, Trash2, Download, ArrowLeft, Settings } from 'lucide-react';

const CoupaBatchPage = ({ 
  files, 
  batchData: externalBatchData,
  setBatchData: setExternalBatchData,
  user, 
  extractInvoiceBackend, 
  logTransaction,
  exportFormat,
  setExportFormat,
  onBack
}) => {
  const processingStartedRef = useRef(false);
  const batchIdRef = useRef(null);
  const mountedRef = useRef(true);
  // Use parent state instead of local state
const batchData = externalBatchData;
const setBatchData = setExternalBatchData;
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [showJson, setShowJson] = useState(false);
  const [showDefaultConfig, setShowDefaultConfig] = useState(false);
  const [selectedInvoices, setSelectedInvoices] = useState(new Set());
  const [defaultConfig, setDefaultConfig] = useState({
    supplierNumber: '',
    requesterEmail: '',
    chartOfAccounts: '',
    currency: 'USD',
    shipToId: '',
    shipToCountryCode: 'US',
    remitToCode: '',
    paymentTerms: ''
  });

useEffect(() => {
  mountedRef.current = true;
  
  // Only initialize if batchData is empty and we have files
  if (batchData.length === 0 && files.length > 0) {
    const currentBatchId = `batch_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    batchIdRef.current = currentBatchId;
    processingStartedRef.current = false;
    
    const initBatch = files.map((file, index) => {
      const existingItem = batchData.find(item => item.fileName === file.name);
      return {
        id: `${currentBatchId}_${index}`,
        file: file,
        fileName: file.name,
        status: 'pending',
        pdfUrl: existingItem?.pdfUrl || URL.createObjectURL(file),
        invoiceData: null,
        error: null,
        batchId: currentBatchId
      };
    });
    
    setBatchData(initBatch);
    
    if (!processingStartedRef.current) {
      processingStartedRef.current = true;
      processAllFiles(initBatch, currentBatchId);
    }
  } else if (batchData.length > 0 && files.length === 0) {
    // If batchData exists but no files, user removed all - clear and exit
    setBatchData([]);
    onBack();
  }
  
  return () => {
    mountedRef.current = false;
    // Don't revoke URLs - they're needed when navigating back
  };
}, [files, batchData.length, setBatchData]);


useEffect(() => {
  const handleBeforeUnload = (e) => {
    if (batchData.length > 0) {
      e.preventDefault();
      e.returnValue = 'You have unsaved batch data. If you leave, all progress will be lost.';
      return 'You have unsaved batch data. If you leave, all progress will be lost.';
    }
  };

  window.addEventListener('beforeunload', handleBeforeUnload);

  return () => {
    window.removeEventListener('beforeunload', handleBeforeUnload);
  };
}, [batchData.length]);

useEffect(() => {
  if (!user) {
    setBatchData([]);
    onBack();
  }
}, [user, setBatchData, onBack]);


const processAllFiles = useCallback(async (initialData, batchId) => {
  if (batchId !== batchIdRef.current) {
    console.log('Stale batch detected, skipping processing');
    return;
  }
  
  for (let i = 0; i < initialData.length; i++) {
    if (!mountedRef.current || batchId !== batchIdRef.current) {
      console.log('Processing cancelled: component unmounted or new batch started');
      break;
    }
    await processFile(i, initialData, batchId);
  }
}, []);


const processFile = async (index, dataArray = null, batchId = null) => {
  if (batchId && batchId !== batchIdRef.current) {
    console.log(`Skipping file ${index}: stale batch`);
    return;
  }
  
  const currentData = dataArray || batchData;
  
  if (currentData[index]?.status === 'completed' || currentData[index]?.status === 'processing') {
    console.log(`Skipping file ${index}: already ${currentData[index].status}`);
    return;
  }
  
  if (!mountedRef.current) return;
  
  setBatchData(prev => prev.map((item, idx) => 
    idx === index ? { ...item, status: 'processing' } : item
  ));

  try {
    const file = currentData[index]?.file || files[index];
    
    const reader = new FileReader();
    const base64Promise = new Promise((resolve, reject) => {
      reader.onload = (event) => resolve(event.target.result.split(',')[1]);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

    const base64 = await base64Promise;
    
    if (!mountedRef.current || (batchId && batchId !== batchIdRef.current)) {
      return;
    }
    
    const extractedData = await extractInvoiceBackend(base64);

    if (!mountedRef.current) return;

    setBatchData(prev => prev.map((item, idx) => 
      idx === index ? { 
        ...item, 
        status: 'completed',
        invoiceData: extractedData,
        error: null
      } : item
    ));

    if (extractedData['invoice-number']) {
      await logTransaction(extractedData['invoice-number'], file.name);
    }

  } catch (error) {
    if (!mountedRef.current) return;
    
    setBatchData(prev => prev.map((item, idx) => 
      idx === index ? { 
        ...item, 
        status: 'error',
        error: error.message
      } : item
    ));
  }
};


const applyDefaultConfig = async () => {
  if (!user) return;
  
  if (selectedInvoices.size === 0) {
    alert('Please select at least one invoice to apply configuration');
    return;
  }
  
  try {
    // Read from userConfigs collection
    const configRef = doc(db, 'userConfigs', user.id);
    const configSnap = await getDoc(configRef);
    
    if (!configSnap.exists()) {
      alert('⚠️ No saved configuration found. Please set it up in the Configuration tab first.');
      return;
    }
    
    const config = configSnap.data();
    
    setBatchData(prev => prev.map((item, index) => {
      // Only apply to selected invoices
      if (!selectedInvoices.has(index)) {
        return item;
      }
      
      if (item.status === 'completed' && item.invoiceData) {
        const updatedData = { ...item.invoiceData };
        
        // Apply all header-level configuration fields
        if (config.chartOfAccount) {
          updatedData['account-type'].name = config.chartOfAccount;
        }
        
        if (config.supplierNumber) {
          updatedData.supplier.number = config.supplierNumber;
        }
        
        if (config.shipToId) {
          updatedData['ship-to-address'].id = config.shipToId;
        }
        
        if (config.remitToCode) {
          updatedData['remit-to-address']['remit-to-code'] = config.remitToCode;
        }
        
        // Apply account code ONLY to lines missing PO Line Number
        const accountCode = config.fullAccountCode || '';
        if (accountCode) {
          updatedData['invoice-lines'].forEach(line => {
            if (!line['order-line-num'] || line['order-line-num'].trim() === '') {
              line.account = {
                code: accountCode,
                'account-type': { name: config.chartOfAccount || '' }
              };
            }
          });
        }
        
        return { ...item, invoiceData: updatedData };
      }
      return item;
    }));
    
    alert(`✅ Configuration applied to ${selectedInvoices.size} selected invoice(s)!`);
    setSelectedInvoices(new Set()); // Clear selection
    setShowDefaultConfig(false);
  } catch (error) {
    console.error('Error applying default config:', error);
    alert('Failed to apply default configuration');
  }
};


const retryFile = async (index) => {
  await processFile(index, null, batchIdRef.current);
};

 const removeFile = (index) => {
  const fileName = batchData[index]?.fileName || 'this file';
  if (window.confirm(`Are you sure you want to remove "${fileName}"?`)) {
    const updatedData = batchData.filter((_, idx) => idx !== index);
    setBatchData(updatedData);
    
    if (selectedIndex === index) {
      setSelectedIndex(null);
    }
    
    // If all files removed, go back to upload page
    if (updatedData.length === 0) {
      onBack();
    }
  }
};

  const updateInvoiceData = (index, newData) => {
    setBatchData(prev => prev.map((item, idx) => 
      idx === index ? { ...item, invoiceData: newData } : item
    ));
  };

  const handleBack = () => {
  if (batchData.length > 0 && batchData.some(item => item.status === 'processing' || item.status === 'completed')) {
    const confirmMsg = 'Go back to main screen? Your batch data will be saved and you can return to it.';
    if (window.confirm(confirmMsg)) {
      onBack();
    }
  } else {
    onBack();
  }
};
  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending': return <Clock className="w-5 h-5 text-gray-400" />;
      case 'processing': return <Loader2 className="w-5 h-5 text-blue-600 animate-spin" />;
      case 'completed': return <CheckCircle className="w-5 h-5 text-indigo-600" />;
      case 'error': return <XCircle className="w-5 h-5 text-red-600" />;
      default: return null;
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'pending': return 'Waiting...';
      case 'processing': return 'Processing...';
      case 'completed': return 'Completed';
      case 'error': return 'Failed';
      default: return '';
    }
  };

  const completedCount = batchData.filter(item => item.status === 'completed').length;
  const errorCount = batchData.filter(item => item.status === 'error').length;
  const processingCount = batchData.filter(item => item.status === 'processing').length;

  const exportAllCoupaCSV = () => {
    const completedInvoices = batchData.filter(item => item.status === 'completed' && item.invoiceData);
    
    if (completedInvoices.length === 0) {
      alert('No completed invoices to export');
      return;
    }

    const escapeCSV = (value) => {
      if (value == null || value === '') return '';
      const str = String(value);
      if (str.includes(',') || str.includes('"') || str.includes('\n')) {
        return `"${str.replace(/"/g, '""')}"`;
      }
      return str;
    };

    let csv = '';
    
    csv += 'Invoice,Invoice Number,Supplier Number,Invoice Date,Submit For Approval?,Handling Amount,Misc Amount,Shipping Amount,Discount Amount,Payment Terms,Requester Email,Requester Lookup Name,Chart of Accounts,Currency,Image Scan Filename,Ship to Id,Ship To Name,Ship To Attention,Ship To Street1,Ship To Street2,Ship To City,Ship To State,Ship To Postal Code,Ship to Country Code,Remit To Code,Original invoice number,Original invoice date,Is Credit Note,Attachment 1,Attachment 2,Attachment 3,Attachment 4,Attachment 5\n';
    
    csv += 'Invoice Line,Invoice Number,Supplier Number,Line Number,Description,Supplier Part Number,Auxiliary Part Number,Price,Quantity,Unit of Measure,PO Number,PO Line Number,Account Name,Account Segment 1,Account Segment 2,Account Segment 3,Account Segment 4,Account Segment 5,Account Segment 6,Account Segment 7,Account Segment 8,Account Segment 9,Account Segment 10,Account Segment 11,Account Segment 12,Account Segment 13,Account Segment 14,Account Segment 15,Account Segment 16,Account Segment 17,Account Segment 18,Account Segment 19,Account Segment 20\n';
    
    csv += 'Invoice Tax Line,Invoice Line Number,Tax Amount,Tax Rate,Tax Code,Tax Rate Type\n';
    
    completedInvoices.forEach(item => {
      const inv = item.invoiceData;
      const handlingAmount = (inv['handling-amount'] && inv['handling-amount'] !== '') ? parseFloat(inv['handling-amount']) : '';
      const miscAmount = (inv['misc-amount'] && inv['misc-amount'] !== '') ? parseFloat(inv['misc-amount']) : '';
      const shippingAmount = (inv['shipping-amount'] && inv['shipping-amount'] !== '') ? parseFloat(inv['shipping-amount']) : '';
      const discountAmount = (inv['discount-amount'] && inv['discount-amount'] !== '') ? parseFloat(inv['discount-amount']) : '';

      csv += `Invoice,${escapeCSV(inv['invoice-number'])},${escapeCSV(inv.supplier.number)},${escapeCSV(inv['invoice-date'])},,${handlingAmount},${miscAmount},${shippingAmount},${discountAmount},${escapeCSV(inv['payment-terms'])},${escapeCSV(inv['requester-email'])},,"${escapeCSV(inv['account-type'].name)}",${escapeCSV(inv.currency.code)},${escapeCSV(item.fileName)},${escapeCSV(inv['ship-to-address'].id)},,,,,,,,,${escapeCSV(inv['remit-to-address']['remit-to-code'])},,,,,,,,\n`;
      
      inv['invoice-lines'].forEach(line => {
        const segments = line.account?.code?.split('-') || [];
        csv += `Invoice Line,${escapeCSV(inv['invoice-number'])},${escapeCSV(inv.supplier.number)},${escapeCSV(line['line-num'])},${escapeCSV(line.description)},${escapeCSV(line['supplier-part-number'])},${escapeCSV(line['auxiliary-part-number'])},${escapeCSV(line.price)},${escapeCSV(line.quantity)},${escapeCSV(line.uom.code)},${escapeCSV(line['po-number'])},${escapeCSV(line['order-line-num'])},,${escapeCSV(segments[0])},${escapeCSV(segments[1])},${escapeCSV(segments[2])},${escapeCSV(segments[3])},${escapeCSV(segments[4])},${escapeCSV(segments[5])},${escapeCSV(segments[6])},${escapeCSV(segments[7])},${escapeCSV(segments[8])},${escapeCSV(segments[9])},${escapeCSV(segments[10])},${escapeCSV(segments[11])},${escapeCSV(segments[12])},${escapeCSV(segments[13])},${escapeCSV(segments[14])},${escapeCSV(segments[15])},${escapeCSV(segments[16])},${escapeCSV(segments[17])},${escapeCSV(segments[18])},${escapeCSV(segments[19])}\n`;
      });
      
      inv['invoice-lines'].forEach(line => {
        if (line['tax-lines'] && line['tax-lines'].length > 0) {
          line['tax-lines'].forEach(tax => {
            csv += `Invoice Tax Line,${escapeCSV(line['line-num'])},${escapeCSV(tax.amount)},${escapeCSV(tax.rate)},,\n`;
          });
        }
      });

      if (inv['tax-lines'] && inv['tax-lines'].length > 0) {
        inv['tax-lines'].forEach(tax => {
          csv += `Invoice Tax Line,,${escapeCSV(tax.amount)},${escapeCSV(tax.rate)},,\n`;
        });
      }
    });

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `coupa_batch_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  // Detail view for single file
  if (selectedIndex !== null && batchData[selectedIndex]) {
    const selectedItem = batchData[selectedIndex];
    
    return (
      <div className="space-y-2">
        <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-3">
          <button
            onClick={() => setSelectedIndex(null)}
            className="flex items-center gap-2 px-4 py-2 text-sm bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Batch List
          </button>
          <div className="mt-2 flex items-center justify-between">
            <div>
              <p className="text-sm font-semibold text-gray-900">{selectedItem.fileName}</p>
              <p className="text-xs text-gray-500">File {selectedIndex + 1} of {batchData.length}</p>
            </div>
            <div className="flex items-center gap-2">
              {selectedIndex > 0 && (
                <button
                  onClick={() => setSelectedIndex(selectedIndex - 1)}
                  className="px-3 py-1.5 text-xs bg-indigo-100 text-indigo-700 rounded-lg hover:bg-indigo-200"
                >
                  ← Previous
                </button>
              )}
              {selectedIndex < batchData.length - 1 && (
                <button
                  onClick={() => setSelectedIndex(selectedIndex + 1)}
                  className="px-3 py-1.5 text-xs bg-indigo-100 text-indigo-700 rounded-lg hover:bg-indigo-200"
                >
                  Next →
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-2">
            <h3 className="text-sm font-semibold text-gray-900 mb-2">Invoice Preview</h3>
            <div className="bg-gray-100 rounded-lg h-[calc(100vh-180px)] overflow-hidden">
              <iframe 
                src={selectedItem.pdfUrl} 
                className="w-full h-full rounded-lg border-0"
                title="Invoice Preview"
              />
            </div>
          </div>

          <div className="space-y-2">
            <CoupaEditor
                            key={selectedIndex}
                            invoiceData={selectedItem.invoiceData}
                            setInvoiceData={(newData) => updateInvoiceData(selectedIndex, newData)}
                            pdfFile={selectedItem.file}
                            showJson={showJson}
                            setShowJson={setShowJson}
                            user={user}
                            exportFormat={exportFormat}
                            setExportFormat={setExportFormat}
                          />
          </div>
        </div>
      </div>
    );
  }

  // Batch list view
  return (
    <div className="space-y-2">
      <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={handleBack}
              className="flex items-center gap-2 px-3 py-1.5 text-xs bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </button>
            <h2 className="text-base font-bold text-gray-900 flex items-center gap-2">
              <span className="text-lg">💙</span>
              Coupa Batch Processing
            </h2>
            <div className="flex items-center gap-3 text-xs">
              <span className="px-2 py-1 bg-blue-50 text-blue-700 rounded font-medium">
                Total: {batchData.length}
              </span>
              <span className="px-2 py-1 bg-indigo-50 text-indigo-700 rounded font-medium">
                ✓ {completedCount}
              </span>
              <span className="px-2 py-1 bg-amber-50 text-amber-700 rounded font-medium">
                ⏳ {processingCount}
              </span>
              <span className="px-2 py-1 bg-red-50 text-red-700 rounded font-medium">
                ✗ {errorCount}
              </span>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            {completedCount > 0 && (
              <>
                <button
                  onClick={() => setShowDefaultConfig(true)}
                  className="flex items-center gap-1.5 px-4 py-2 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium"
                >
                  <Settings className="w-4 h-4" />
                  Apply Default Config
                </button>
                <button
                  onClick={exportAllCoupaCSV}
                  className="flex items-center gap-1.5 px-4 py-2 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors font-medium"
                >
                  <Download className="w-4 h-4" />
                  Export All as CSV ({completedCount})
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Default Config Modal */}
{showDefaultConfig && (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
    <div className="bg-white rounded-2xl shadow-2xl p-6 max-w-2xl w-full mx-4 max-h-[80vh] overflow-y-auto">
      <h3 className="text-xl font-bold text-gray-900 mb-4">Apply Saved Default Configuration</h3>
      <p className="text-sm text-gray-600 mb-6">
        Select which invoices you want to apply your saved configuration to. This will populate Supplier Number, Ship-To ID, Remit-To Code, Chart of Accounts, and account codes for non-PO lines.
      </p>
      
      {/* Invoice Selection */}
      <div className="mb-6 max-h-[300px] overflow-y-auto border border-gray-200 rounded-lg">
        <div className="sticky top-0 bg-gray-50 px-4 py-3 border-b border-gray-200 flex items-center gap-2">
          <input
            type="checkbox"
            checked={selectedInvoices.size === completedCount && completedCount > 0}
            onChange={(e) => {
              if (e.target.checked) {
                const allCompleted = new Set();
                batchData.forEach((item, index) => {
                  if (item.status === 'completed') {
                    allCompleted.add(index);
                  }
                });
                setSelectedInvoices(allCompleted);
              } else {
                setSelectedInvoices(new Set());
              }
            }}
            className="w-4 h-4 text-indigo-600 rounded"
          />
          <span className="text-sm font-medium text-gray-700">
            Select All ({completedCount} completed)
          </span>
        </div>
        
        <div className="divide-y divide-gray-200">
          {batchData.map((item, index) => {
            if (item.status !== 'completed') return null;
            
            return (
              <label 
                key={index}
                className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={selectedInvoices.has(index)}
                  onChange={(e) => {
                    const newSelected = new Set(selectedInvoices);
                    if (e.target.checked) {
                      newSelected.add(index);
                    } else {
                      newSelected.delete(index);
                    }
                    setSelectedInvoices(newSelected);
                  }}
                  className="w-4 h-4 text-indigo-600 rounded"
                />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">{item.fileName}</p>
                  <p className="text-xs text-gray-500">
                    Invoice: {item.invoiceData?.['invoice-number'] || 'N/A'} | 
                    Supplier: {item.invoiceData?.supplier?.number || 'N/A'}
                  </p>
                </div>
              </label>
            );
          })}
        </div>
      </div>
      
      <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-3 mb-4">
        <p className="text-xs text-indigo-800">
          <strong>{selectedInvoices.size}</strong> invoice(s) selected. Configuration will apply saved values for Supplier Number, Ship-To, Remit-To, Chart of Accounts, and account codes to lines missing PO Line Numbers.
        </p>
      </div>
      
      <div className="flex justify-end gap-3">
        <button
          onClick={() => {
            setShowDefaultConfig(false);
            setSelectedInvoices(new Set());
          }}
          className="px-4 py-2 text-sm text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
        >
          Cancel
        </button>
        <button
          onClick={applyDefaultConfig}
          disabled={selectedInvoices.size === 0}
          className="px-4 py-2 text-sm text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Apply to Selected ({selectedInvoices.size})
        </button>
      </div>
    </div>
  </div>
)}

      <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-5">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Invoice Files</h3>
        
        <div className="space-y-3">
          {batchData.map((item, index) => (
            <div 
              key={item.id} 
              className="border border-gray-200 rounded-lg p-4 hover:border-indigo-300 transition-colors"
            >
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 mt-1">
                  {getStatusIcon(item.status)}
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="text-sm font-semibold text-gray-900 truncate">
                      {item.fileName}
                    </h4>
                    <span className={`px-2 py-1 text-xs font-medium rounded ${
                      item.status === 'completed' ? 'bg-indigo-100 text-indigo-700' :
                      item.status === 'processing' ? 'bg-blue-100 text-blue-700' :
                      item.status === 'error' ? 'bg-red-100 text-red-700' :
                      'bg-gray-100 text-gray-700'
                    }`}>
                      {getStatusText(item.status)}
                    </span>
                  </div>

                  {item.status === 'completed' && item.invoiceData && (
  <div className="flex items-center justify-between gap-2 p-2 bg-indigo-50 rounded border border-indigo-200 text-xs">
    <div className="flex items-center gap-4 flex-1 min-w-0">
      <span className="text-indigo-700 font-medium">Invoice:</span>
      <span className="text-gray-900 font-semibold">{item.invoiceData['invoice-number'] || 'N/A'}</span>
      
      <span className="text-indigo-700 font-medium">Supplier:</span>
      <span className="text-gray-900 font-semibold truncate max-w-[130px]" title={item.invoiceData.supplier?.name || item.invoiceData.supplier?.number || 'N/A'}>
        {item.invoiceData.supplier?.name || item.invoiceData.supplier?.number || 'N/A'}
      </span>
      
      <span className="text-indigo-700 font-medium">PO:</span>
      <span className="text-gray-900 font-semibold">{item.invoiceData['invoice-lines']?.[0]?.['po-number'] || 'N/A'}</span>
      
      <span className="text-indigo-700 font-medium">Total:</span>
      <span className="text-gray-900 font-semibold">
        {item.invoiceData.currency?.code || 'USD'} {item.invoiceData['custom-fields']?.['invoice-total'] || '0.00'}
      </span>
      
      <span className="text-indigo-700 font-medium">Date:</span>
      <span className="text-gray-900 font-semibold">{item.invoiceData['invoice-date'] || 'N/A'}</span>
    </div>
    
    <div className="flex items-center gap-2 flex-shrink-0">
      <button
        onClick={() => setSelectedIndex(index)}
        className="flex items-center gap-1 px-2 py-1 bg-indigo-600 text-white rounded hover:bg-indigo-700"
      >
        <Eye className="w-3 h-3" />
        View
      </button>
      <button
        onClick={() => removeFile(index)}
        className="flex items-center gap-1 px-2 py-1 bg-red-100 text-red-700 rounded hover:bg-red-200"
      >
        <Trash2 className="w-3 h-3" />
        Remove
      </button>
    </div>
  </div>
)}

{item.status === 'error' && item.error && (
  <div className="p-2 bg-red-50 border border-red-200 rounded text-xs text-red-700">
    Error: {item.error}
  </div>
)}

<div className="flex items-center gap-2">
  {item.status === 'error' && (
    <button
      onClick={() => retryFile(index)}
      className="flex items-center gap-1 px-2 py-1 text-xs bg-amber-600 text-white rounded hover:bg-amber-700"
    >
      <Loader2 className="w-3 h-3" />
      Retry
    </button>
  )}
</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default CoupaBatchPage;